import { createAppStore } from '@app/core/stores';

export type YoutubeStatsSettingsStoreState = {
  /** Channels currently enabled by the user across all stations. */
  selectedChannelIds: string[];
  /** Tracks which channels were seen at least once to auto-enable new channels by default. */
  knownChannelIds: string[];
  toggleChannel: (channelId: string) => void;
  selectChannels: (channelIds: string[]) => void;
  clearChannels: (channelIds: string[]) => void;
  syncAvailableChannels: (channelIds: string[]) => void;
};

/**
 * Persists the global YouTube stats channel selection so legend toggles are remembered across stations.
 */
export const useYoutubeStatsSettingsStore = createAppStore<
  YoutubeStatsSettingsStoreState,
  Pick<YoutubeStatsSettingsStoreState, 'selectedChannelIds' | 'knownChannelIds'>
>('YoutubeStatsSettingsStore', {
  persist: {
    name: 'app.youtubeStatsSettings',
    version: 1,
    partialize: (state) => ({
      selectedChannelIds: state.selectedChannelIds,
      knownChannelIds: state.knownChannelIds,
    }),
  },
})((set) => ({
  selectedChannelIds: [],
  knownChannelIds: [],
  toggleChannel: (channelId) => {
    set((state) => {
      const selectedChannelIds = state.selectedChannelIds.includes(channelId)
        ? state.selectedChannelIds.filter((id) => id !== channelId)
        : [...state.selectedChannelIds, channelId];

      const knownChannelIds = state.knownChannelIds.includes(channelId)
        ? state.knownChannelIds
        : [...state.knownChannelIds, channelId];

      return {
        selectedChannelIds,
        knownChannelIds,
      };
    });
  },
  selectChannels: (channelIds) => {
    set((state) => {
      const nextSelected = new Set(state.selectedChannelIds);
      const nextKnown = new Set(state.knownChannelIds);

      channelIds.forEach((channelId) => {
        nextSelected.add(channelId);
        nextKnown.add(channelId);
      });

      return {
        selectedChannelIds: Array.from(nextSelected),
        knownChannelIds: Array.from(nextKnown),
      };
    });
  },
  clearChannels: (channelIds) => {
    set((state) => ({
      selectedChannelIds: state.selectedChannelIds.filter((id) => !channelIds.includes(id)),
      knownChannelIds: state.knownChannelIds,
    }));
  },
  syncAvailableChannels: (channelIds) => {
    set((state) => {
      const nextKnown = new Set(state.knownChannelIds);
      const nextSelected = new Set(state.selectedChannelIds);
      let hasNewChannel = false;

      channelIds.forEach((channelId) => {
        if (!nextKnown.has(channelId)) {
          hasNewChannel = true;
          nextKnown.add(channelId);
          nextSelected.add(channelId);
        }
      });

      if (!hasNewChannel) {
        return state;
      }

      return {
        knownChannelIds: Array.from(nextKnown),
        selectedChannelIds: Array.from(nextSelected),
      };
    });
  },
}));
